import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Award, ArrowRight, ArrowLeft } from 'lucide-react';

export const Hospital: React.FC = () => {
    const images = [
        "/hospital1.png", // Hall
        "/hospital2.png", // Room
        "/hospital3.png", // Equipment
        "/hospital4.png"  // Exterior
    ];

    const [currentImage, setCurrentImage] = useState(0);

    // Auto-advance slider
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentImage((prev) => (prev + 1) % images.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [images.length]);

    const nextImage = () => setCurrentImage((prev) => (prev + 1) % images.length);
    const prevImage = () => setCurrentImage((prev) => (prev - 1 + images.length) % images.length);

    return (
        <section className="py-20 md:py-32 bg-white">
            <div className="container mx-auto px-6">

                <div className="mb-12 md:mb-16 flex flex-col gap-8 md:gap-16 items-center">
                    <div className="w-full text-center">
                        <span className="text-[#11BC97] font-bold tracking-widest uppercase text-sm mb-4 block flex items-center justify-center gap-2">
                            <span className="w-8 h-[1px] bg-[#11BC97]"></span>
                            Rehab Facility
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-black max-w-3xl mx-auto">World Class Infrastructure</h2>
                    </div>
                    <div className="w-full flex justify-center">
                        <div className="max-w-3xl text-gray-700 text-base md:text-lg font-light text-center leading-relaxed px-4 md:px-0">
                            Our rehabilitation facility offers state-of-the-art rooms with orthopaedic beds, Smart TVs, complimentary Wi-Fi and ensuite bathrooms. Qualified, English-speaking teams provide round-the-clock care, and on-site amenities include a fully equipped gym and café. Situated close to the boutiques and restaurants of the stylish Nişantaşı Quarter, the facility combines clinical excellence with convenient access to the city's upscale attractions.
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-none md:grid-rows-2 gap-6 h-auto md:h-[800px] mt-10">
                    {/* Main Image Slider Block */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="col-span-1 md:col-span-2 row-span-1 md:row-span-2 relative rounded-[2rem] md:rounded-[2.5rem] overflow-hidden group bg-gray-100 min-h-[500px] md:min-h-0"
                    >
                        <AnimatePresence mode='wait'>
                            <motion.img 
                                key={currentImage}
                                src={images[currentImage]}
                                initial={{ opacity: 0, scale: 1.1 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7 }}
                                alt="Rehab Facility" 
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </AnimatePresence>
                        
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        
                        {/* Slider Controls */}
                        <div className="absolute top-6 right-6 md:top-8 md:right-8 flex gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={prevImage} className="p-2 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white hover:text-black transition-colors">
                                <ArrowLeft size={20} />
                            </button>
                            <button onClick={nextImage} className="p-2 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white hover:text-black transition-colors">
                                <ArrowRight size={20} />
                            </button>
                        </div>

                        <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 text-white max-w-xl pr-6">
                            <div className="flex gap-2 mb-4">
                                {images.map((_, idx) => (
                                    <div 
                                        key={idx} 
                                        className={`h-1 rounded-full transition-all duration-300 ${idx === currentImage ? 'w-8 bg-[#11BC97]' : 'w-2 bg-white/50'}`} 
                                    />
                                ))}
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold mb-2 md:mb-4">Exceptional Care</h3>
                            <p className="text-base md:text-lg text-white/90">Our team delivers world-class rehabilitation and support in a modern, comfortable environment.</p>
                        </div>
                    </motion.div>

                    {/* Earthquake Proof Block */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="col-span-1 bg-[#F8FDFB] p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] border border-[#11BC97]/10 flex flex-col justify-between hover:border-[#11BC97]/30 transition-colors min-h-[250px]"
                    >
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-[#11BC97]/10 rounded-2xl flex items-center justify-center text-[#11BC97]">
                            <ShieldCheck className="w-7 h-7 md:w-8 md:h-8" />
                        </div>
                        <div>
                            <h4 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Built for wellbeing</h4>
                            <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                                Internationally-recognised both for its futuristic earthquake-proof technology, and for its health-and-wellness focused design.
                            </p>
                        </div>
                    </motion.div>

                    {/* Awards Block */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="col-span-1 bg-white p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100 flex flex-col justify-between relative overflow-hidden group hover:shadow-xl transition-all duration-300 min-h-[250px]"
                    >
                        <div className="relative z-10">
                            <div className="w-12 h-12 md:w-14 md:h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-400 group-hover:bg-[#11BC97] group-hover:text-white transition-colors mb-4 md:mb-6">
                                <Award className="w-7 h-7 md:w-8 md:h-8" />
                            </div>
                            <h4 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Award Winning</h4>
                            <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                                Boasts some of Turkey's very best medical facilities and the very latest medical technologies for optimal recovery.
                            </p>
                        </div>
                         <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#11BC97]/5 rounded-full blur-2xl group-hover:bg-[#11BC97]/10 transition-colors" />
                    </motion.div>
                </div>
            </div>
        </section>
    );
};